import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { ConsultantWholesaleModalComponent } from '@consultant/consultant-wholesale-modal/consultant-wholesale-modal.component';
import { DefaultConsultantImagePlaceholderSrc } from '@core/constants/image-placeholders';
import { FindConsultantRequest, LocateConsultantRequest } from '@core/dto/consultant.dto';
import { ConsultantFinderType } from '@core/enums/consultant-finder-type.enum';
import { AppState } from '@core/store';
import { selectConsultantFinder } from '@core/store/consultant';
import { Consultant, ConsultantFinder } from '@core/store/consultant/consultant-state-models';
import {
  fetchCurrentConsultantByVanityName,
  findConsultant,
  locateConsultant,
} from '@core/store/consultant/consultant.actions';
import { Store, select } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-consultant-finder-result',
  templateUrl: './consultant-finder-result.component.html',
  styleUrls: ['./consultant-finder-result.component.scss'],
})
export class ConsultantFinderResultComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() filter: FindConsultantRequest | LocateConsultantRequest;
  @Input() finderType: ConsultantFinderType;
  @Input() loadMoreButtonIsVisible: boolean = true;
  @Input() pageSearch: boolean = false;
  @Output() consultantSelected: EventEmitter<void> = new EventEmitter<void>();

  readonly consultantFinderType = ConsultantFinderType;
  readonly imagePlaceholder = DefaultConsultantImagePlaceholderSrc;
  readonly ButtonText = {
    [ConsultantFinderType.find]: $localize`Load more`,
    [ConsultantFinderType.locate]: $localize`Search again`,
  };

  consultantFinder$: Observable<ConsultantFinder>;
  loading$: Observable<boolean>;
  consultants$: Observable<Consultant[]>;
  WholeSaleConsultantText: string = $localize`Cannot Accept eCommerce Orders.`;

  @ViewChild('listWrapper') private listWrapper: ElementRef;
  @ViewChild('consModal') private consModal: ConsultantWholesaleModalComponent;

  private destroy$ = new Subject<void>();

  constructor(
    private store$: Store<AppState>,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.consultantFinder$ = this.store$.select(selectConsultantFinder);
    this.loading$ = this.consultantFinder$.pipe(select((s) => s.loading));
    this.consultants$ = this.consultantFinder$.pipe(select((s) => s.results));
  }

  ngAfterViewInit(): void {
    this.consultants$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.scrollToBottom();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  selectConsultant(consultant: Consultant) {
    this.store$.dispatch(
      fetchCurrentConsultantByVanityName({
        vanityName: consultant.vanityName,
        filterCountry: true,
      }),
    );
    this.consultantSelected.emit();
  }

  contactConsultant(consultant: Consultant) {
    this.consModal.open(consultant);
  }

  consultantMessageSent(consultant: Consultant) {
    this.store$.dispatch(
      fetchCurrentConsultantByVanityName({
        vanityName: consultant.vanityName,
        filterCountry: true,
      }),
    );
    this.consultantSelected.emit();
    this.router.navigateByUrl('/');
  }

  consultantMessageNotSent() {
    return;
  }

  loadMore() {
    if (this.finderType === ConsultantFinderType.find) {
      this.store$.dispatch(findConsultant({ payload: this.filter as FindConsultantRequest }));
    } else if (this.finderType === ConsultantFinderType.locate) {
      this.store$.dispatch(
        locateConsultant({
          payload: {
            ...this.filter,
          } as LocateConsultantRequest,
        }),
      );
    }
  }

  private scrollToBottom(): void {
    if (this.listWrapper?.nativeElement) {
      // Defer scroll to allow DOM to fully render after change detection
      setTimeout(() => {
        this.listWrapper.nativeElement.scroll({
          top: this.listWrapper.nativeElement.scrollHeight,
          left: 0,
          behavior: 'smooth',
        });
      }, 0);
    }
  }
}
